/**
 * First-party, cookieless traffic beacon.
 *
 * Sends aggregate-only events (pageview / CTA click) to the `trackLanding`
 * Cloud Function, which increments per-day counters in Firestore. Nothing
 * identifying leaves the browser: no cookie, no client id, no user agent
 * beyond what any HTTP request carries — which is why this fires without a
 * consent gate, unlike the opt-in GA4/PostHog stacks. The server normalizes
 * paths/labels against an allowlist, so junk input cannot mint new counters.
 *
 * `sendBeacon` is used so clicks that unload the page (store links, app
 * links) still deliver; it falls back to a keepalive fetch.
 */

const BEACON_URL =
    'https://europe-west1-acesense-prod.cloudfunctions.net/trackLanding';

type BeaconEvent = 'pageview' | 'cta_click';

export function sendLandingBeacon(
    event: BeaconEvent,
    detail: { path?: string; label?: string } = {},
): void {
    // Prerender/SSR: no navigator, nothing to send with. (A window-less
    // environment that still has navigator does not exist in practice.)
    if (typeof navigator === 'undefined') {
        return;
    }
    if (window.location.hostname === 'localhost') return;
    const payload = JSON.stringify({
        e: event,
        p: detail.path ?? window.location.pathname,
        l: detail.label ?? '',
    });
    try {
        if (navigator.sendBeacon?.(BEACON_URL, payload)) return;
    } catch {
        // fall through to fetch
    }
    void fetch(BEACON_URL, {
        method: 'POST',
        body: payload,
        keepalive: true,
        headers: { 'Content-Type': 'text/plain' },
    }).catch(() => undefined);
}
